import { app, Analytics } from './firebase-config.js';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export const auth = getAuth(app);
export const db = getFirestore(app, 'default');

const googleProvider = new GoogleAuthProvider();

// Auth error messages
const AUTH_ERRORS = {
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/too-many-requests': 'Too many attempts. Try again later.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/email-not-verified': 'Please verify your email before signing in. Check your inbox (and spam folder).',
};

export function getAuthError(code) {
  return AUTH_ERRORS[code] || 'Something went wrong. Please try again.';
}

async function computeGravatarHash(email) {
  const data = new TextEncoder().encode(email.trim().toLowerCase());
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Guard a page: redirect to /login if not authenticated or email not verified.
// `returnTo` carries the page back through login; without it the login page sends
// everyone to Studio, which is the wrong landing for a page they asked for.
export function requireAuth(onUser, { returnTo = '' } = {}) {
  const loginUrl = returnTo ? `/login?return=${encodeURIComponent(returnTo)}` : '/login';
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      window.location.href = loginUrl;
      return;
    }
    if (!user.emailVerified) {
      await signOut(auth);
      window.location.href = loginUrl;
      return;
    }
    let profile = null;
    try {
      profile = await getUserProfile(user.uid);
    } catch (err) {
      console.error('[auth] Failed to load user profile:', err.message);
    }
    Analytics.identifyUser(user.uid, {
      plan: profile?.plan || 'free',
      handle: profile?.username || null,
    });
    onUser(user, profile);
  });
}

// Guard the login page: redirect to the author studio (or an explicit return URL)
export function redirectIfAuth(returnUrl = '/studio') {
  return onAuthStateChanged(auth, (user) => {
    if (user && user.emailVerified) {
      window.location.href = returnUrl;
    }
  });
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

export async function loginWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  if (!cred.user.emailVerified) {
    await signOut(auth);
    const err = new Error('Email not verified');
    err.code = 'auth/email-not-verified';
    throw err;
  }
  Analytics.trackSignIn('email');
  return cred.user;
}

export async function registerWithEmail(email, password, displayName) {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(user, { displayName });
  // Create user profile in Firestore
  await setDoc(doc(db, 'users', user.uid), {
    uid: user.uid,
    email,
    displayName,
    username: '',
    plan: 'free',
    bio: '',
    expertiseLine: '',
    primaryContentUrl: '',
    availabilityEnabled: false,
    availabilityConfig: {},
    gravatarHash: await computeGravatarHash(email),
    createdAt: serverTimestamp(),
  });
  await sendEmailVerification(user);
  await signOut(auth);
  Analytics.trackSignUp('email');
  return { verificationSent: true };
}

export async function resendVerificationEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  if (!cred.user.emailVerified) {
    await sendEmailVerification(cred.user);
  }
  await signOut(auth);
}

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

export async function loginWithGoogle() {
  const { user } = await signInWithPopup(auth, googleProvider);
  // Create profile if first time
  const existing = await getUserProfile(user.uid);
  if (!existing) {
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      username: '',
      plan: 'free',
      bio: '',
      expertiseLine: '',
      primaryContentUrl: '',
      availabilityEnabled: false,
      availabilityConfig: {},
      gravatarHash: await computeGravatarHash(user.email),
      createdAt: serverTimestamp(),
    });
    Analytics.trackSignUp('google');
  } else {
    Analytics.trackSignIn('google');
  }
  return user;
}

export async function logout() {
  await signOut(auth);
  window.location.href = '/login';
}
